import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";

const List = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs uppercase tracking-widest text-stone-500">{title}</p>
    <ul className="mt-2 space-y-1 text-sm text-stone-700">{(items || []).map((x, i) => <li key={i}>• {x}</li>)}</ul>
  </div>
);

export const BuddyInsights = ({ classId, studentId }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/classes/${classId}/students/${studentId}/buddy`;
  useEffect(() => { http.get(base).then((r) => setD(r.data)).catch(() => {}); }, [base]);
  if (!d) return null;

  const summarize = async () => {
    setBusy(true);
    try { const r = await http.post(`${base}/summary`); setD((x) => ({ ...x, summary: r.data })); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <section className="mt-10 rounded-3xl bg-white/60 border border-[#E6DEC3] p-6" data-testid="buddy-insights">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-stone-500">Learning buddy</p>
          <h3 className="font-display text-2xl text-[#2C4A3E] mt-1" data-testid="buddy-insights-name">{d.has_buddy ? d.buddy_name : "No buddy yet"}</h3>
          <p className="text-xs text-stone-500 mt-1 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Conversations stay private. You see summaries and safety flags only.</p>
        </div>
        {d.has_buddy && <button onClick={summarize} disabled={busy} data-testid="buddy-summary-btn" className="h-fit inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2C4A3E] text-white text-sm disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {d.summary ? "Refresh summary" : "Generate summary"}</button>}
      </div>
      {d.flags.length > 0 && (
        <div className="mt-5 rounded-2xl bg-red-50 border border-red-200 p-4" data-testid="buddy-flags">
          <p className="text-sm font-semibold text-red-700 inline-flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> {d.flags.length} safety flag{d.flags.length > 1 ? "s" : ""} - please check in with this student</p>
          <ul className="mt-2 space-y-1.5 text-sm text-red-800">
            {d.flags.map((f) => <li key={f.id} data-testid={`buddy-flag-${f.id}`}>{f.created_at.slice(0, 10)} · {f.reason} ({f.source.replaceAll("_", " ")}): <i>"{f.excerpt}"</i></li>)}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3 mt-5 text-center">
        {[["chats", "Message"], ["sorts", "Thought sort"]].map(([k, l]) => <div key={k} className="rounded-2xl bg-[#F7F3EB] p-3"><div className="font-display text-xl text-[#2C4A3E]" data-testid={`buddy-stat-${k}`}>{d[k]}</div><div className="text-[11px] uppercase tracking-widest text-stone-500">{l}{d[k] === 1 ? "" : "s"}</div></div>)}
        <div className="rounded-2xl bg-[#F7F3EB] p-3"><div className="font-display text-xl text-[#2C4A3E]" data-testid="buddy-stat-plans">{d.plans.filter((p) => p.done === p.total).length}/{d.plans.length}</div><div className="text-[11px] uppercase tracking-widest text-stone-500">Plans finished</div></div>
      </div>
      {d.summary && (
        <div className="grid md:grid-cols-3 gap-5 mt-6" data-testid="buddy-summary">
          <p className="md:col-span-3 font-display text-[#2C4A3E]">{d.summary.overall}</p>
          <List title="Themes" items={d.summary.themes} testId="buddy-summary-themes" />
          <List title="Strengths" items={d.summary.strengths} testId="buddy-summary-strengths" />
          <List title="How you can help" items={d.summary.support_ideas} testId="buddy-summary-support" />
        </div>
      )}
      {d.goals.length > 0 && <div className="mt-5"><List title="Student's goals" items={d.goals} testId="buddy-goals" /></div>}
    </section>
  );
};
